import { ACCOUNTS_API_URL, ALL_ACCOUNTS_API_URL } from "../config/constants";

import { parseAccountResponse } from "../pages/Accounts/accounts.utils";
import { apiFetch } from "../utils/apiClient";

const getAuthHeader = () => {
  const rawToken =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");

  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

  if (!rawToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  const normalizedToken = rawToken
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  return `${tokenType} ${normalizedToken}`;
};

const buildHeaders = () => ({
  Authorization: getAuthHeader(),
  "Content-Type": "application/json",
  Accept: "application/json",
});

const extractErrorMessage = async (
  response,
  fallback
) => {
  try {
    const contentType = response.headers.get("content-type") || "";

    // Backend returns JSON
    if (contentType.includes("application/json")) {
      const data = await response.json();

      // Example:
      // { "message": "Account already exists" }
      if (
        typeof data?.message === "string" &&
        data.message.trim()
      ) {
        return data.message.trim();
      }

      // Example:
      // { "error": "Account already exists" }
      if (
        typeof data?.error === "string" &&
        data.error.trim()
      ) {
        return data.error.trim();
      }

      // Example:
      // { "detail": "Account already exists" }
      if (
        typeof data?.detail === "string" &&
        data.detail.trim()
      ) {
        return data.detail.trim();
      }

      // Validation error:
      // { "errors": ["Account name is required"] }
      if (Array.isArray(data?.errors)) {
        return data.errors
          .map((error) => String(error))
          .join(", ");
      }

      // Validation error:
      // { "errors": { "accountName": "Account already exists" } }
      if (
        data?.errors &&
        typeof data.errors === "object"
      ) {
        return Object.values(data.errors)
          .flat()
          .map((error) => String(error))
          .join(", ");
      }
    }

    // Backend returns plain text
    const text = await response.text();

    if (text.trim()) {
      return text.trim();
    }
  } catch {
    // Ignore parsing errors and use fallback
  }

  return fallback;
};

const extractSuccessMessage = async (
  response,
  fallback
) => {
  try {
    const raw = await response.text();
    const trimmed = raw.trim();

    if (!trimmed) {
      return fallback;
    }

    try {
      const data = JSON.parse(trimmed) 
      

      return (
        data.message ||
        data.successMessage ||
        data.data?.message ||
        fallback
      );
    } catch {
      return trimmed;
    }
  } catch {
    return fallback;
  }
};

export async function getAccounts(
  params
) {
  const requestUrl = new URL(
    ALL_ACCOUNTS_API_URL,
    globalThis.location.origin
  );

  requestUrl.searchParams.set(
    "page",
    String(Math.max(params.page - 1, 0))
  );

  requestUrl.searchParams.set(
    "size",
    String(params.pageSize)
  );

  const response = await apiFetch(requestUrl.toString(), {
    method: "GET",
    headers: buildHeaders(),
  }, "Failed to load accounts.");

  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(
        response,
        `Failed to load accounts (${response.status}).`
      )
    );
  }

  const contentType =
    response.headers.get("content-type") || "";

  const payload = contentType.includes("application/json")
    ? await response.json()
    : [];

  return parseAccountResponse(payload);
}

export async function addAccount(
  payload
) {
  const response = await apiFetch(ACCOUNTS_API_URL, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({
      accountName: payload.accountName.trim(),
    }),
  }, "Failed to create account.");

  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(
        response,
        `Failed to create account (${response.status}).`
      )
    );
  }

  return extractSuccessMessage(
    response,
    "Account created successfully."
  );
}

export async function updateAccount(
  accountId,
  payload
) {
  const response = await apiFetch(
    `${ACCOUNTS_API_URL}/${encodeURIComponent(accountId)}`,
    {
      method: "PUT",
      headers: buildHeaders(),
      body: JSON.stringify({
        accountName: payload.accountName.trim(),
        status: payload.status,
      }),
    },
    "Failed to update account.",
  );

  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(
        response,
        `Failed to update account (${response.status}).`
      )
    );
  }

  return extractSuccessMessage(
    response,
    "Account updated successfully."
  );
}

export async function removeAccount(
  accountId
) {
  const response = await apiFetch(
    `${ACCOUNTS_API_URL}/${encodeURIComponent(accountId)}`,
    {
      method: "DELETE",
      headers: buildHeaders(),
    },
    "Failed to delete account.",
  );

  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(
        response,
        `Failed to delete account (${response.status}).`
      )
    );
  }

  return extractSuccessMessage(
    response,
    "Account deleted successfully."
  );
}